import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ChartBarIcon,
  UsersIcon,
  CurrencyDollarIcon,
  TruckIcon,
  DocumentChartBarIcon,
  ArrowPathIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import apiService from '../../services/api';
import { FormField, DateInput, Select, DateDisplay } from '../../components/Forms';
import { downloadExcelCsv } from '../../utils/exportExcel';
import { formatAppDate, getLocalToday } from '../../utils/dateDisplay';

const toLocalDate = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

type RangePreset = 'month' | 'quarter' | 'year';

const RANGE_PRESETS: { key: RangePreset; label: string }[] = [
  { key: 'month', label: 'هذا الشهر' },
  { key: 'quarter', label: 'آخر 3 أشهر' },
  { key: 'year', label: 'هذه السنة' },
];

const presetRange = (key: RangePreset) => {
  const now = new Date();
  const start =
    key === 'month'
      ? new Date(now.getFullYear(), now.getMonth(), 1)
      : key === 'quarter'
        ? new Date(now.getFullYear(), now.getMonth() - 3, now.getDate())
        : new Date(now.getFullYear(), 0, 1);
  return { startDate: toLocalDate(start), endDate: getLocalToday() };
};

interface ReportFilters {
  startDate: string;
  endDate: string;
  reportType: 'overview' | 'customers' | 'trips' | 'financial' | 'bookings';
}

const Reports: React.FC = () => {
  // Default to the whole year so older bookings are not hidden
  const [filters, setFilters] = useState<ReportFilters>(() => ({
    ...presetRange('year'),
    reportType: 'overview',
  }));

  const applyPreset = (key: RangePreset) => {
    setFilters((prev) => ({ ...prev, ...presetRange(key) }));
  };

  const activePreset = RANGE_PRESETS.find((p) => {
    const r = presetRange(p.key);
    return r.startDate === filters.startDate && r.endDate === filters.endDate;
  })?.key;

  const [activeTab, setActiveTab] = useState<'overview' | 'customers' | 'trips' | 'financial' | 'bookings'>('overview');

  // Overview Report Query
  const { data: overviewData, isLoading: overviewLoading, error: overviewError, refetch: refetchOverview } = useQuery({
    queryKey: ['reports', 'overview', filters.startDate, filters.endDate],
    queryFn: () => apiService.getReportsOverview({
      startDate: filters.startDate,
      endDate: filters.endDate
    }),
    enabled: activeTab === 'overview'
  });

  // Customer Reports Query
  const { data: customerData, isLoading: customerLoading, error: customerError, refetch: refetchCustomers } = useQuery({
    queryKey: ['reports', 'customers', filters.startDate, filters.endDate],
    queryFn: () => apiService.getCustomerReports({
      startDate: filters.startDate,
      endDate: filters.endDate,
      limit: 20
    }),
    enabled: activeTab === 'customers'
  });

  // Trip Reports Query
  const { data: tripData, isLoading: tripLoading, error: tripError, refetch: refetchTrips } = useQuery({
    queryKey: ['reports', 'trips', filters.startDate, filters.endDate],
    queryFn: () => apiService.getTripReports({
      startDate: filters.startDate,
      endDate: filters.endDate
    }),
    enabled: activeTab === 'trips'
  });

  // Financial Reports Query
  const { data: financialData, isLoading: financialLoading, error: financialError, refetch: refetchFinancial } = useQuery({
    queryKey: ['reports', 'financial', filters.startDate, filters.endDate],
    queryFn: () => apiService.getFinancialReports(filters.startDate, filters.endDate),
    enabled: activeTab === 'financial'
  });

  // Booking Analytics Query
  const { data: bookingData, isLoading: bookingLoading, error: bookingError, refetch: refetchBookings } = useQuery({
    queryKey: ['reports', 'bookings', filters.startDate, filters.endDate],
    queryFn: () => apiService.getBookingAnalytics({
      startDate: filters.startDate,
      endDate: filters.endDate,
      groupBy: 'day'
    }),
    enabled: activeTab === 'bookings'
  });

  const handleFilterChange = (field: keyof ReportFilters, value: string) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleTabChange = (tab: typeof activeTab) => {
    setActiveTab(tab);
  };

  const refetchCurrentReport = () => {
    switch (activeTab) {
      case 'overview':
        refetchOverview();
        break;
      case 'customers':
        refetchCustomers();
        break;
      case 'trips':
        refetchTrips();
        break;
      case 'financial':
        refetchFinancial();
        break;
      case 'bookings':
        refetchBookings();
        break;
    }
  };

  const exportCurrentReportToExcel = () => {
    const range = `${filters.startDate}_${filters.endDate}`;

    if (activeTab === 'overview' && overviewData) {
      const statusRows = (overviewData.bookingsByStatus || []).map((item: any) => [
        getStatusLabel(item._id),
        item.count,
        item.revenue,
      ]);
      const tripTypeRows = (overviewData.tripTypes || []).map((item: any) => [
        getTripTypeLabel(item._id),
        item.count,
        item.revenue,
      ]);
      const paymentRows = (overviewData.paymentMethods || []).map((item: any) => [
        getPaymentMethodLabel(item._id),
        item.count,
        item.amount,
      ]);

      downloadExcelCsv(
        `تقرير_نظرة_عامة_${range}`,
        ['القسم', 'البند', 'العدد', 'المبلغ'],
        [
          ['ملخص', 'إجمالي الحجوزات', overviewData.totals?.bookings || 0, ''],
          ['ملخص', 'إجمالي الإيرادات', '', overviewData.totals?.revenue || 0],
          ['ملخص', 'المبلغ المدفوع', '', overviewData.totals?.paidAmount || 0],
          ['ملخص', 'المبلغ المتبقي', '', overviewData.totals?.pendingAmount || 0],
          ...statusRows.map((r: unknown[]) => ['حسب الحالة', ...r]),
          ...tripTypeRows.map((r: unknown[]) => ['نوع الرحلة', ...r]),
          ...paymentRows.map((r: unknown[]) => ['طريقة الدفع', ...r]),
        ]
      );
      return;
    }

    if (activeTab === 'customers' && customerData) {
      downloadExcelCsv(
        `تقرير_العملاء_${range}`,
        ['الاسم', 'الجوال', 'عدد الحجوزات', 'إجمالي الإنفاق', 'المبلغ المدفوع', 'آخر حجز'],
        (customerData.topCustomers || []).map((customer: any) => [
          customer._id?.name || '',
          customer._id?.phone || '',
          customer.bookings || 0,
          customer.totalSpent || 0,
          customer.totalPaid || 0,
          formatAppDate(customer.lastBooking, 'gregorian') || '',
        ])
      );
      return;
    }

    if (activeTab === 'trips' && tripData) {
      downloadExcelCsv(
        `تقرير_الرحلات_${range}`,
        [
          'رقم الرحلة',
          'من',
          'إلى',
          'الحجوزات',
          'المسافرون',
          'السعة',
          'معدل الإشغال %',
          'الإيرادات',
        ],
        (tripData.tripPerformance || []).map((trip: any) => [
          trip.tripNumber || '',
          trip.route?.from || '',
          trip.route?.to || '',
          trip.bookings || 0,
          trip.passengers || 0,
          trip.capacity || 0,
          Math.round(trip.occupancyRate || 0),
          trip.revenue || 0,
        ])
      );
      return;
    }

    if (activeTab === 'financial' && financialData) {
      downloadExcelCsv(
        `تقرير_المالية_${range}`,
        ['القسم', 'البند', 'العدد', 'الإيرادات', 'المدفوع'],
        [
          ['ملخص', 'إجمالي الحجوزات', financialData.totals?.bookings || 0, '', ''],
          ['ملخص', 'إجمالي الإيرادات', '', financialData.totals?.revenue || 0, ''],
          ['ملخص', 'المبلغ المدفوع', '', '', financialData.totals?.paidAmount || 0],
          ['ملخص', 'المبلغ المتبقي', '', '', financialData.totals?.pendingAmount || 0],
          ...(financialData.byPaymentMethod || []).map((item: any) => [
            'طريقة الدفع',
            getPaymentMethodLabel(item._id),
            item.count,
            item.revenue,
            item.paidAmount,
          ]),
          ...(financialData.dailyTrend || []).map((item: any) => [
            'يومي',
            item._id,
            item.bookings,
            item.revenue,
            item.paidAmount,
          ]),
        ]
      );
      return;
    }

    if (activeTab === 'bookings' && bookingData) {
      downloadExcelCsv(
        `تقرير_الحجوزات_${range}`,
        ['الفترة', 'الحالة', 'نوع الرحلة', 'عدد الحجوزات', 'الإيرادات', 'المدفوع'],
        (bookingData.analytics || []).map((item: any) => [
          item._id?.period || '',
          getStatusLabel(item._id?.status),
          getTripTypeLabel(item._id?.tripType),
          item.count || 0,
          item.revenue || 0,
          item.paidAmount || 0,
        ])
      );
      return;
    }

    alert('لا توجد بيانات جاهزة للتصدير في هذا التبويب حالياً');
  };

  const formatCurrency = (amount: unknown) => {
    const value = typeof amount === 'number' ? amount : Number(amount);
    const safe = Number.isFinite(value) ? value : 0;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'SAR',
      minimumFractionDigits: 0
    }).format(safe).replace(/SAR\s*/g, 'ر.س ');
  };

  const getStatusColor = (status: string) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
      completed: 'bg-blue-100 text-blue-800',
      refunded: 'bg-gray-100 text-gray-800'
    };
    return colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status: string) => {
    const labels = {
      pending: 'في انتظار التأكيد',
      confirmed: 'مؤكد',
      cancelled: 'ملغي',
      completed: 'مكتمل',
      refunded: 'مسترد'
    };
    return labels[status as keyof typeof labels] || status;
  };

  const getTripTypeLabel = (type: string) => {
    const labels = {
      hajj: 'حج',
      umrah: 'عمرة',
      transport: 'نقل'
    };
    return labels[type as keyof typeof labels] || type;
  };

  const getPaymentMethodLabel = (method: string) => {
    const labels = {
      cash: 'نقدي',
      card: 'بطاقة',
      transfer: 'تحويل',
      partial: 'مختلط'
    };
    return labels[method as keyof typeof labels] || method;
  };

  const tabs = [
    { id: 'overview', label: 'نظرة عامة', icon: ChartBarIcon },
    { id: 'customers', label: 'العملاء', icon: UsersIcon },
    { id: 'trips', label: 'الرحلات', icon: TruckIcon },
    { id: 'financial', label: 'المالية', icon: CurrencyDollarIcon },
    { id: 'bookings', label: 'الحجوزات', icon: DocumentChartBarIcon }
  ];

  const isLoading = overviewLoading || customerLoading || tripLoading || financialLoading || bookingLoading;

  const activeError = {
    overview: overviewError,
    customers: customerError,
    trips: tripError,
    financial: financialError,
    bookings: bookingError,
  }[activeTab] as any;
  const activeErrorMessage = activeError
    ? activeError?.response?.data?.message ||
      (activeError?.response?.status === 403
        ? 'ليس لديك صلاحية لعرض التقارير'
        : activeError?.message || 'تعذر تحميل التقرير')
    : '';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">التقارير</h1>
          <p className="mt-1 text-sm text-gray-500">تقارير شاملة حول أداء النشاط التجاري</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={exportCurrentReportToExcel}
            className="btn-primary inline-flex items-center"
            disabled={isLoading}
          >
            <ArrowDownTrayIcon className="w-5 h-5" />
            تصدير إلى إكسل
          </button>
          <button
            type="button"
            onClick={refetchCurrentReport}
            className="btn-secondary inline-flex items-center"
            disabled={isLoading}
          >
            <ArrowPathIcon className="w-5 h-5" />
            تحديث البيانات
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-gray-500">الفترة:</span>
          {RANGE_PRESETS.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => applyPreset(p.key)}
              className={`rounded-full px-3 py-1 text-sm transition-colors ${
                activePreset === p.key
                  ? 'bg-primary-700 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <DateInput
            label="تاريخ البداية"
            compactToggle
            value={filters.startDate}
            onChange={(e) => handleFilterChange('startDate', e.target.value)}
          />
          <DateInput
            label="تاريخ النهاية"
            compactToggle
            value={filters.endDate}
            onChange={(e) => handleFilterChange('endDate', e.target.value)}
          />
          <FormField label="نوع التقرير">
            <Select
              value={activeTab}
              onChange={(e) => handleTabChange(e.target.value as typeof activeTab)}
              options={[
                { value: 'overview', label: 'نظرة عامة' },
                { value: 'customers', label: 'تقارير العملاء' },
                { value: 'trips', label: 'تقارير الرحلات' },
                { value: 'financial', label: 'التقارير المالية' },
                { value: 'bookings', label: 'تحليل الحجوزات' }
              ]}
            />
          </FormField>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 space-x-reverse px-6">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id as typeof activeTab)}
                  className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-5 h-5 ml-2" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              <span className="mr-3 text-gray-600">جاري تحميل البيانات...</span>
            </div>
          ) : activeErrorMessage ? (
            <div className="flex flex-col items-center gap-3 py-12 text-center">
              <p className="text-sm text-red-700">{activeErrorMessage}</p>
              <button type="button" onClick={refetchCurrentReport} className="btn-secondary py-2 text-sm">
                <ArrowPathIcon className="w-4 h-4" />
                إعادة المحاولة
              </button>
            </div>
          ) : (
            <>
              {activeTab === 'overview' && overviewData && !(overviewData.totals?.bookings > 0) && (
                <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-800">
                  <span>لا توجد حجوزات بين التاريخين المحددين. جرب فترة أطول.</span>
                  {activePreset !== 'year' && (
                    <button
                      type="button"
                      onClick={() => applyPreset('year')}
                      className="font-medium text-amber-900 underline"
                    >
                      عرض هذه السنة
                    </button>
                  )}
                </div>
              )}

              {/* Overview Tab */}
              {activeTab === 'overview' && overviewData && (
                <div className="space-y-6">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div className="bg-blue-50 rounded-lg p-6">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <DocumentChartBarIcon className="h-8 w-8 text-blue-600" />
                        </div>
                        <div className="mr-5 w-0 flex-1">
                          <dl>
                            <dt className="text-sm font-medium text-gray-500 truncate">
                              إجمالي الحجوزات
                            </dt>
                            <dd className="text-lg font-medium text-gray-900">
                              {overviewData.totals?.bookings || 0}
                            </dd>
                          </dl>
                        </div>
                      </div>
                    </div>

                    <div className="bg-green-50 rounded-lg p-6">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <CurrencyDollarIcon className="h-8 w-8 text-green-600" />
                        </div>
                        <div className="mr-5 w-0 flex-1">
                          <dl>
                            <dt className="text-sm font-medium text-gray-500 truncate">
                              إجمالي الإيرادات
                            </dt>
                            <dd className="text-lg font-medium text-gray-900">
                              {formatCurrency(overviewData.totals?.revenue || 0)}
                            </dd>
                          </dl>
                        </div>
                      </div>
                    </div>

                    <div className="bg-yellow-50 rounded-lg p-6">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <CurrencyDollarIcon className="h-8 w-8 text-yellow-600" />
                        </div>
                        <div className="mr-5 w-0 flex-1">
                          <dl>
                            <dt className="text-sm font-medium text-gray-500 truncate">
                              المبلغ المدفوع
                            </dt>
                            <dd className="text-lg font-medium text-gray-900">
                              {formatCurrency(overviewData.totals?.paidAmount || 0)}
                            </dd>
                          </dl>
                        </div>
                      </div>
                    </div>

                    <div className="bg-red-50 rounded-lg p-6">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <CurrencyDollarIcon className="h-8 w-8 text-red-600" />
                        </div>
                        <div className="mr-5 w-0 flex-1">
                          <dl>
                            <dt className="text-sm font-medium text-gray-500 truncate">
                              المبلغ المتبقي
                            </dt>
                            <dd className="text-lg font-medium text-gray-900">
                              {formatCurrency(overviewData.totals?.pendingAmount || 0)}
                            </dd>
                          </dl>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Charts Row */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Bookings by Status */}
                    <div className="bg-gray-50 rounded-lg p-6">
                      <h3 className="text-lg font-medium text-gray-900 mb-4">الحجوزات حسب الحالة</h3>
                      <div className="space-y-3">
                        {overviewData.bookingsByStatus?.map((item: any) => (
                          <div key={item._id} className="flex items-center justify-between">
                            <div className="flex items-center">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(item._id)}`}>
                                {getStatusLabel(item._id)}
                              </span>
                              <span className="mr-3 text-sm text-gray-600">
                                {item.count} حجز
                              </span>
                            </div>
                            <span className="text-sm font-medium text-gray-900">
                              {formatCurrency(item.revenue)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Trip Types */}
                    <div className="bg-gray-50 rounded-lg p-6">
                      <h3 className="text-lg font-medium text-gray-900 mb-4">أنواع الرحلات</h3>
                      <div className="space-y-3">
                        {overviewData.tripTypes?.map((item: any) => (
                          <div key={item._id} className="flex items-center justify-between">
                            <div className="flex items-center">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                {getTripTypeLabel(item._id)}
                              </span>
                              <span className="mr-3 text-sm text-gray-600">
                                {item.count} حجز
                              </span>
                            </div>
                            <span className="text-sm font-medium text-gray-900">
                              {formatCurrency(item.revenue)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Payment Methods */}
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">طرق الدفع</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {overviewData.paymentMethods?.map((item: any) => (
                        <div key={item._id} className="bg-white rounded-lg p-4 border">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-900">
                              {getPaymentMethodLabel(item._id)}
                            </span>
                            <span className="text-sm text-gray-600">
                              {item.count} معاملة
                            </span>
                          </div>
                          <div className="mt-2">
                            <span className="text-lg font-bold text-primary-600">
                              {formatCurrency(item.amount)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Customers Tab */}
              {activeTab === 'customers' && customerData && (
                <div className="space-y-6">
                  {/* Customer Statistics */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="bg-blue-50 rounded-lg p-6">
                      <div className="flex items-center">
                        <UsersIcon className="h-8 w-8 text-blue-600" />
                        <div className="mr-4">
                          <p className="text-2xl font-bold text-gray-900">
                            {customerData.statistics?.uniqueCustomers || 0}
                          </p>
                          <p className="text-sm text-gray-600">عميل فريد</p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-green-50 rounded-lg p-6">
                      <div className="flex items-center">
                        <UsersIcon className="h-8 w-8 text-green-600" />
                        <div className="mr-4">
                          <p className="text-2xl font-bold text-gray-900">
                            {customerData.statistics?.repeatCustomers || 0}
                          </p>
                          <p className="text-sm text-gray-600">عميل متكرر</p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-yellow-50 rounded-lg p-6">
                      <div className="flex items-center">
                        <ChartBarIcon className="h-8 w-8 text-yellow-600" />
                        <div className="mr-4">
                          <p className="text-2xl font-bold text-gray-900">
                            {customerData.statistics?.customerRetentionRate || 0}%
                          </p>
                          <p className="text-sm text-gray-600">معدل الاحتفاظ</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Top Customers */}
                  <div className="bg-white border rounded-lg">
                    <div className="px-6 py-4 border-b border-gray-200">
                      <h3 className="text-lg font-medium text-gray-900">أفضل العملاء</h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              العميل
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              عدد الحجوزات
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              إجمالي الإنفاق
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              المبلغ المدفوع
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              آخر حجز
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {customerData.topCustomers?.map((customer: any, index: number) => (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div>
                                  <div className="text-sm font-medium text-gray-900">
                                    {customer._id.name}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {customer._id.phone}
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {customer.bookings}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {formatCurrency(customer.totalSpent)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {formatCurrency(customer.totalPaid)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                <DateDisplay value={customer.lastBooking} />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Trips Tab */}
              {activeTab === 'trips' && tripData && (
                <div className="space-y-6">
                  {/* Trip Performance */}
                  <div className="bg-white border rounded-lg">
                    <div className="px-6 py-4 border-b border-gray-200">
                      <h3 className="text-lg font-medium text-gray-900">أداء الرحلات</h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              رقم الرحلة
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              المسار
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              الحجوزات
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              المسافرون
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              معدل الإشغال
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              الإيرادات
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {tripData.tripPerformance?.map((trip: any) => (
                            <tr key={trip._id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {trip.tripNumber}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {trip.route?.from} → {trip.route?.to}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {trip.bookings}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {trip.passengers} / {trip.capacity}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <div className="flex-1 bg-gray-200 rounded-full h-2 ml-3">
                                    <div
                                      className="bg-primary-600 h-2 rounded-full"
                                      style={{ width: `${Math.min(trip.occupancyRate || 0, 100)}%` }}
                                    ></div>
                                  </div>
                                  <span className="text-sm text-gray-900">
                                    {Math.round(trip.occupancyRate || 0)}%
                                  </span>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {formatCurrency(trip.revenue)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Route Popularity */}
                  <div className="bg-white border rounded-lg">
                    <div className="px-6 py-4 border-b border-gray-200">
                      <h3 className="text-lg font-medium text-gray-900">شعبية المسارات</h3>
                    </div>
                    <div className="p-6">
                      <div className="space-y-4">
                        {tripData.routePopularity?.map((route: any, index: number) => (
                          <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                            <div>
                              <p className="font-medium text-gray-900">
                                {route._id.from} → {route._id.to}
                              </p>
                              <p className="text-sm text-gray-600">
                                {route.bookings} حجز • {route.passengers} مسافر
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="font-medium text-gray-900">
                                {formatCurrency(route.revenue)}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Financial Tab */}
              {activeTab === 'financial' && financialData && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div className="bg-blue-50 rounded-lg p-6">
                      <p className="text-sm text-gray-500">إجمالي الحجوزات</p>
                      <p className="mt-1 text-2xl font-bold text-gray-900">
                        {financialData.totals?.bookings || 0}
                      </p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-6">
                      <p className="text-sm text-gray-500">إجمالي الإيرادات</p>
                      <p className="mt-1 text-2xl font-bold text-gray-900">
                        {formatCurrency(financialData.totals?.revenue || 0)}
                      </p>
                    </div>
                    <div className="bg-emerald-50 rounded-lg p-6">
                      <p className="text-sm text-gray-500">المبلغ المدفوع</p>
                      <p className="mt-1 text-2xl font-bold text-gray-900">
                        {formatCurrency(financialData.totals?.paidAmount || 0)}
                      </p>
                    </div>
                    <div className="bg-amber-50 rounded-lg p-6">
                      <p className="text-sm text-gray-500">المبلغ المتبقي</p>
                      <p className="mt-1 text-2xl font-bold text-gray-900">
                        {formatCurrency(financialData.totals?.pendingAmount || 0)}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-gray-50 rounded-lg p-6">
                      <h3 className="text-lg font-medium text-gray-900 mb-4">حسب طريقة الدفع</h3>
                      {(financialData.byPaymentMethod || []).length === 0 ? (
                        <p className="text-sm text-gray-500">لا توجد بيانات في الفترة المحددة</p>
                      ) : (
                        <div className="space-y-3">
                          {financialData.byPaymentMethod.map((item: any) => (
                            <div key={String(item._id)} className="flex items-center justify-between bg-white rounded-lg border px-4 py-3">
                              <div>
                                <p className="text-sm font-medium text-gray-900">
                                  {getPaymentMethodLabel(item._id)}
                                </p>
                                <p className="text-xs text-gray-500">{item.count} حجز</p>
                              </div>
                              <div className="text-left">
                                <p className="text-sm font-semibold text-gray-900">
                                  {formatCurrency(item.paidAmount || 0)}
                                </p>
                                <p className="text-xs text-gray-500">
                                  إجمالي {formatCurrency(item.revenue || 0)}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="bg-gray-50 rounded-lg p-6">
                      <h3 className="text-lg font-medium text-gray-900 mb-4">حسب حالة الحجز</h3>
                      {(financialData.byStatus || []).length === 0 ? (
                        <p className="text-sm text-gray-500">لا توجد بيانات في الفترة المحددة</p>
                      ) : (
                        <div className="space-y-3">
                          {financialData.byStatus.map((item: any) => (
                            <div key={String(item._id)} className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(item._id)}`}>
                                  {getStatusLabel(item._id)}
                                </span>
                                <span className="text-sm text-gray-600">{item.count} حجز</span>
                              </div>
                              <span className="text-sm font-medium text-gray-900">
                                {formatCurrency(item.revenue || 0)}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="bg-white border rounded-lg">
                    <div className="px-6 py-4 border-b border-gray-200">
                      <h3 className="text-lg font-medium text-gray-900">الاتجاه اليومي</h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500">التاريخ</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500">الحجوزات</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500">الإيرادات</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500">المدفوع</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {(financialData.dailyTrend || []).length === 0 ? (
                            <tr>
                              <td colSpan={4} className="px-6 py-8 text-center text-sm text-gray-500">
                                لا توجد بيانات في الفترة المحددة
                              </td>
                            </tr>
                          ) : (
                            financialData.dailyTrend.map((row: any) => (
                              <tr key={row._id} className="hover:bg-gray-50">
                                <td className="px-6 py-3 text-sm text-gray-900">{row._id}</td>
                                <td className="px-6 py-3 text-sm text-gray-900">{row.bookings}</td>
                                <td className="px-6 py-3 text-sm text-gray-900">{formatCurrency(row.revenue || 0)}</td>
                                <td className="px-6 py-3 text-sm text-gray-900">{formatCurrency(row.paidAmount || 0)}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Bookings Tab */}
              {activeTab === 'bookings' && bookingData && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div className="bg-blue-50 rounded-lg p-6">
                      <p className="text-sm text-gray-500">إجمالي الحجوزات</p>
                      <p className="mt-1 text-2xl font-bold text-gray-900">
                        {bookingData.totals?.bookings || 0}
                      </p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-6">
                      <p className="text-sm text-gray-500">إجمالي الإيرادات</p>
                      <p className="mt-1 text-2xl font-bold text-gray-900">
                        {formatCurrency(bookingData.totals?.revenue || 0)}
                      </p>
                    </div>
                    <div className="bg-emerald-50 rounded-lg p-6">
                      <p className="text-sm text-gray-500">المبلغ المدفوع</p>
                      <p className="mt-1 text-2xl font-bold text-gray-900">
                        {formatCurrency(bookingData.totals?.paidAmount || 0)}
                      </p>
                    </div>
                    <div className="bg-amber-50 rounded-lg p-6">
                      <p className="text-sm text-gray-500">المبلغ المتبقي</p>
                      <p className="mt-1 text-2xl font-bold text-gray-900">
                        {formatCurrency(bookingData.totals?.pendingAmount || 0)}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-gray-50 rounded-lg p-6">
                      <h3 className="text-lg font-medium text-gray-900 mb-4">حسب الحالة</h3>
                      {(bookingData.byStatus || []).length === 0 ? (
                        <p className="text-sm text-gray-500">لا توجد حجوزات في الفترة المحددة</p>
                      ) : (
                        <div className="space-y-3">
                          {bookingData.byStatus.map((item: any) => (
                            <div key={String(item._id)} className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(item._id)}`}>
                                  {getStatusLabel(item._id)}
                                </span>
                                <span className="text-sm text-gray-600">{item.count} حجز</span>
                              </div>
                              <span className="text-sm font-medium text-gray-900">
                                {formatCurrency(item.revenue || 0)}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="bg-gray-50 rounded-lg p-6">
                      <h3 className="text-lg font-medium text-gray-900 mb-4">حسب نوع الرحلة</h3>
                      {(bookingData.byTripType || []).length === 0 ? (
                        <p className="text-sm text-gray-500">لا توجد حجوزات في الفترة المحددة</p>
                      ) : (
                        <div className="space-y-3">
                          {bookingData.byTripType.map((item: any) => (
                            <div key={String(item._id)} className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  {getTripTypeLabel(item._id)}
                                </span>
                                <span className="text-sm text-gray-600">{item.count} حجز</span>
                              </div>
                              <span className="text-sm font-medium text-gray-900">
                                {formatCurrency(item.revenue || 0)}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="bg-white border rounded-lg">
                    <div className="px-6 py-4 border-b border-gray-200">
                      <h3 className="text-lg font-medium text-gray-900">تحليل يومي</h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500">الفترة</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500">الحجوزات</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500">الإيرادات</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500">المدفوع</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {(bookingData.byPeriod || []).length === 0 ? (
                            <tr>
                              <td colSpan={4} className="px-6 py-8 text-center text-sm text-gray-500">
                                لا توجد حجوزات في الفترة المحددة
                              </td>
                            </tr>
                          ) : (
                            bookingData.byPeriod.map((row: any) => (
                              <tr key={row._id} className="hover:bg-gray-50">
                                <td className="px-6 py-3 text-sm text-gray-900">{row._id}</td>
                                <td className="px-6 py-3 text-sm text-gray-900">{row.count}</td>
                                <td className="px-6 py-3 text-sm text-gray-900">{formatCurrency(row.revenue || 0)}</td>
                                <td className="px-6 py-3 text-sm text-gray-900">{formatCurrency(row.paidAmount || 0)}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="bg-white border rounded-lg">
                    <div className="px-6 py-4 border-b border-gray-200">
                      <h3 className="text-lg font-medium text-gray-900">تفصيل حسب الفترة والحالة والنوع</h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500">الفترة</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500">الحالة</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500">النوع</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500">العدد</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500">الإيرادات</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {(bookingData.analytics || []).length === 0 ? (
                            <tr>
                              <td colSpan={5} className="px-6 py-8 text-center text-sm text-gray-500">
                                لا توجد حجوزات في الفترة المحددة
                              </td>
                            </tr>
                          ) : (
                            bookingData.analytics.map((row: any, idx: number) => (
                              <tr key={`${row._id?.period}-${row._id?.status}-${row._id?.tripType}-${idx}`} className="hover:bg-gray-50">
                                <td className="px-6 py-3 text-sm text-gray-900">{row._id?.period}</td>
                                <td className="px-6 py-3 text-sm">
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(row._id?.status)}`}>
                                    {getStatusLabel(row._id?.status)}
                                  </span>
                                </td>
                                <td className="px-6 py-3 text-sm text-gray-900">{getTripTypeLabel(row._id?.tripType)}</td>
                                <td className="px-6 py-3 text-sm text-gray-900">{row.count}</td>
                                <td className="px-6 py-3 text-sm text-gray-900">{formatCurrency(row.revenue || 0)}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Reports; 